import { InterventionActualisation } from './intervention-actualisation';
import { Injectable, inject, signal, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Authentification } from './authentification';
import { Etat, Parc, Installation, TicketCardItem, TicketColumnState } from './EntiteProjet';
import { Installation as InstallationService } from './installation';
import { ParcService } from './parc';
import { filtresInterventionsMois, reunirInterventions } from './interventions';

@Injectable({ providedIn: 'root' })
export class EtatService {
  private installationService = inject(InstallationService);
  private parcService = inject(ParcService);
  private auth = inject(Authentification);
  private chargementParcsId = 0;
  private chargementInstallationsId = 0;
  private installationsParc: { parc: Parc; promesse: Promise<Installation[]> } | null = null;

  parcs = signal<Parc[]>([]);
  parcSelectionne = signal<Parc | null>(null);
  installations = signal<Installation[]>([]);
  installationCourante = signal<Installation | null>(null);
  nombreInterventionsAVenir = signal(0);

  private chargementInterventionsId = 0;

  public static NIVEAU_HORSLIGNE = 1;
  public static NIVEAU_ARRET = 2;
  public static NIVEAU_PERTE = 3;
  public static NIVEAU_SANS_PERTE = 4;
  public static NIVEAU_NORMAL = 5;

  /** Priorité d'affichage : arrêt, hors ligne, perte, sans perte, normal. */
  public static rangNiveau(niveau: number): number {
    if (niveau === EtatService.NIVEAU_ARRET) return 1;
    if (niveau === EtatService.NIVEAU_HORSLIGNE) return 2;
    return niveau;
  }

  /**
   * Couleur, libellé et classe CSS associés à chaque niveau de criticité.
   * - Couleurs : cf. classes .dot-* dans styles.css.
   * - Libellés/classes de badge : source de vérité pour `statusLabel`/`statusClass` (format.ts).
   */
  public static readonly NIVEAU_INFOS: Record<
    number,
    { couleur: string; label: string; classe: string }
  > = {
    [EtatService.NIVEAU_HORSLIGNE]: {
      couleur: '#1e1e1e',
      label: 'Hors ligne',
      classe: 'horsligne',
    },
    [EtatService.NIVEAU_ARRET]: { couleur: '#f05671', label: "À l'arrêt", classe: 'arret' },
    [EtatService.NIVEAU_PERTE]: { couleur: '#eeaa32', label: 'Avec perte', classe: 'perte' },
    [EtatService.NIVEAU_SANS_PERTE]: {
      couleur: '#7c63ff',
      label: 'Sans perte',
      classe: 'sansperte',
    },
    [EtatService.NIVEAU_NORMAL]: { couleur: '#59c2a6', label: 'Normal', classe: 'normal' },
  };

  /** Déduit le niveau de criticité d'un état à partir de ses indicateurs booléens. */
  public static calculerNiveau(
    etat: Pick<Etat, 'hors_ligne' | 'arret' | 'tickets_avec_pertes' | 'tickets_sans_pertes'>,
  ): number {
    if (etat.hors_ligne) return EtatService.NIVEAU_HORSLIGNE;
    if (etat.arret) return EtatService.NIVEAU_ARRET;
    if (etat.tickets_avec_pertes) return EtatService.NIVEAU_PERTE;
    if (etat.tickets_sans_pertes) return EtatService.NIVEAU_SANS_PERTE;
    return EtatService.NIVEAU_NORMAL;
  }

  constructor() {
    inject(InterventionActualisation)
      .changements.pipe(takeUntilDestroyed())
      .subscribe(() => {
        const parc = this.parcSelectionne();
        if (parc) void this.chargerNombreInterventionsAVenir(parc.id);
      });
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe((connecte) => {
      ++this.chargementParcsId;
      ++this.chargementInstallationsId;
      this.installationsParc = null;
      ++this.chargementInterventionsId;
      this.parcs.set([]);
      this.parcSelectionne.set(null);
      this.installations.set([]);
      this.installationCourante.set(null);
      this.nombreInterventionsAVenir.set(0);
      this.filtreTexteTickets.set('');
      this.ticketSelectionne.set(null);
      this.siloEtatTicket.set(null);
      if (connecte) void this.chargerParcs();
    });
    effect(() => {
      const parc = this.parcSelectionne();
      ++this.chargementInterventionsId;
      this.installationCourante.set(null);
      this.nombreInterventionsAVenir.set(0);
      if (!parc) {
        ++this.chargementInstallationsId;
        this.installationsParc = null;
        this.installations.set([]);
        return;
      }

      void this.obtenirInstallationsParcCourant().catch(() => {});
      void this.chargerNombreInterventionsAVenir(parc.id);
    });
  }

  /** Réutilise la liste du parc courant, y compris son chargement en cours et une liste vide. */
  async obtenirInstallationsParcCourant(): Promise<Installation[]> {
    const parc = this.parcSelectionne();
    if (!parc) throw new Error('Aucun parc sélectionné');
    if (this.installationsParc?.parc === parc) return this.installationsParc.promesse;

    const chargementId = ++this.chargementInstallationsId;
    this.installations.set([]);
    const promesse = this.installationService
      .listeInstallations(parc.id)
      .then((installations) => {
        if (chargementId !== this.chargementInstallationsId || this.parcSelectionne() !== parc)
          throw new Error('Le parc ou la session a changé');
        if (!installations) throw new Error('Liste des installations indisponible');
        this.installations.set(installations);
        return installations;
      })
      .catch((erreur: unknown) => {
        if (chargementId === this.chargementInstallationsId) {
          this.installationsParc = null;
          this.installations.set([]);
        }
        throw erreur;
      });
    this.installationsParc = { parc, promesse };
    return promesse;
  }

  private async chargerNombreInterventionsAVenir(parcId: number): Promise<void> {
    const chargementId = ++this.chargementInterventionsId;
    const maintenant = new Date();
    const aujourdHui = this.toIsoDate(maintenant);

    try {
      const response = await this.parcService.getParcInterventions(
        parcId,
        filtresInterventionsMois(maintenant),
      );
      if (chargementId !== this.chargementInterventionsId || this.parcSelectionne()?.id !== parcId)
        return;

      const nombre = reunirInterventions(response).filter((intervention) => {
        const date = (intervention.date_deb ?? intervention.date_intervention)?.slice(0, 10);
        return intervention.pb_resolu !== true && date != null && date >= aujourdHui;
      }).length;
      this.nombreInterventionsAVenir.set(nombre);
    } catch {
      if (chargementId === this.chargementInterventionsId) {
        this.nombreInterventionsAVenir.set(0);
      }
    }
  }

  private toIsoDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  async chargerParcs(timeoutMs = 3000): Promise<void> {
    const chargementId = ++this.chargementParcsId;
    const parcs = await this.avecTimeout(this.parcService.listeParcs(), timeoutMs);
    if (chargementId !== this.chargementParcsId) return;
    if (!parcs?.length) return;
    const sorted = [...parcs].sort((a, b) => {
      if (a.global !== b.global) return a.global ? -1 : 1;
      return a.libelle.localeCompare(b.libelle);
    });
    this.parcs.set(sorted);
    this.parcSelectionne.set(sorted[0]);
  }

  ajouterParcCree(parc: Parc): void {
    // Une lecture antérieure ne doit pas écraser le parc qui vient d'être créé.
    ++this.chargementParcsId;
    this.parcs.update((parcs) =>
      [...parcs.filter((p) => p.id !== parc.id), parc].sort((a, b) =>
        a.global !== b.global ? (a.global ? -1 : 1) : a.libelle.localeCompare(b.libelle),
      ),
    );
    this.parcSelectionne.set(parc);
  }

  private async avecTimeout<T>(promise: Promise<T>, ms: number): Promise<T | null> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    try {
      return await Promise.race([
        promise.catch(() => null),
        new Promise<null>((resolve) => {
          timer = setTimeout(() => resolve(null), ms);
        }),
      ]);
    } finally {
      clearTimeout(timer);
    }
  }

  // Filtre texte de la page tickets — persiste lors de la navigation
  filtreTexteTickets = signal<string>('');

  // Ticket sélectionné et son silo — partagés entre la liste et la page de détail
  ticketSelectionne = signal<TicketCardItem | null>(null);
  siloEtatTicket = signal<TicketColumnState | null>(null);
}
